import './supOrgPackList.css';
import OrgTripStrap from '../../components/orgTripStrap/OrgTripStrap';
import { useCallback, useEffect, useRef, useState } from 'react';
import ReactLoading from 'react-loading';
import errorHandle from '../../utils/errorHandle';
import { Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { countOfPackages, FetchPackList } from './orgPackListFun/fetchPackList';
import EmptyPage from '../../components/empty-page/emptyPage';
import Pagination from '../../components/pagination/Pagination';
import SupTripStrap from '../../components/supTripStrap/SupTripStrap';
import { supportPackListServiceConfig } from '../../utils/supportServiceConfig/supPackListServiceConfig';
import OrgTripList from '../../components/orgTripList/OrgTripList';
import { supBookingStatusChange, supOrgTripDelFun } from '../../api/supportAdmin';
import { toast } from 'react-toastify';
import ConfirmationModal from '../../components/tripStrap/tripStrapFun/cancelBooking';
import SearchIcon from '../../assets/icons/trvto_searchIconActive.svg';
import AccommodationCard from '../../components/accommodation/AccommodationCard';

const MODE_TO_SERVICE = {
    t: 'trip',
    a: 'act',
    c: 'camp',
    g: 'gu'
};

export default function SupOrgPackList() {
    const navigate = useNavigate();
    const { orgId, mode } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const serviceType = MODE_TO_SERVICE[mode] || 'trip';
    const selectedStatus = searchParams.get('status') || 'active';
    const datesType = searchParams.get('datesType') || 'fixed';
    const group = searchParams.get('group') || 'backpacking';
    const pageNumber = Number(searchParams.get('page')) || 1;
    const [searchRe, setSearchRe] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loading, setLoading] = useState(false);
    // the package list has its own loading flag: the org list / count calls finish at
    // different times and used to hide the spinner while the packages were still coming
    const [listLoading, setListLoading] = useState(false);
    const listRequest = useRef(0);
    const [nav, setNav] = useState(false);
    const [tripData, setTripData] = useState([]);
    const [selectedPack, setSelectedPack] = useState('');
    const selectedOrg = orgId || '';
    const [orgData, setOrgData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const limit = 10;
    const offSet = (pageNumber - 1) * limit;
    const totalData = totalCount;
    const totalPages = Math.ceil(totalData / limit);
    const [load, setLoad] = useState(false);
    const servicePackListConfig = supportPackListServiceConfig[serviceType] || {};
    const [showInactiveStatusModal, setShowInactiveStatusModal] = useState(false);
    const [inactiveId, setInactiveId] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [deleteId, setDeleteId] = useState(null);

    const refresh = () => {
        setLoad(!load);
    };

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchRe.trim());
        }, 500); // 500ms delay

        return () => clearTimeout(timer);
    }, [searchRe]);

    const SERVICE_TO_MODE = {
        trip: 't',
        act: 'a',
        camp: 'c',
        gu: 'g'
    };

    const handleServiceType = (event) => {
        const selectedValue = event.target.value;
        const newMode = SERVICE_TO_MODE[selectedValue];
        const params = new URLSearchParams(searchParams);

        if (selectedValue !== 'trip') {
            params.delete('datesType');
            params.delete('group');
        }
        params.delete('page');
        navigate(`/support/${newMode}/packages?${params.toString()}`);
    };

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);

            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) {
                    delete current[key];
                } else {
                    current[key] = params[key];
                }
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        if (!totalPages) return;

        if (pageNumber > totalPages) {
            updateURL({ page: totalPages });
        }
        //eslint-disable-next-line
    }, [totalPages, pageNumber, updateURL]);

    const paginationClick = (number, offSetNumber) => {
        if (Math.ceil(totalData / limit) > 1) {
            updateURL({ page: number });
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };

    useEffect(() => {
        if (!servicePackListConfig?.fetchOrg) return;
        const fetchOrg = async () => {
            servicePackListConfig.fetchOrg({ serviceType, setOrgData, setLoading: () => {} });
        };
        fetchOrg();
    }, [serviceType]);

    useEffect(() => {
        const request = ++listRequest.current;
        if (!selectedOrg || !servicePackListConfig?.fetchPackList) {
            setTripData([]);
            setListLoading(false);
            return;
        }
        // only the latest request may update the list (an older, slower reply is ignored)
        const isLatest = () => request === listRequest.current;
        async function fetchPackList() {
            setListLoading(true);
            setTripData([]);
            try {
                await servicePackListConfig.fetchPackList({
                    setTripData: (res) => isLatest() && setTripData(res),
                    selectedOrg,
                    selectedStatus,
                    limit,
                    offSet,
                    datesType,
                    group,
                    setLoading: (value) => isLatest() && setListLoading(value),
                    serviceType,
                    searchRe: debouncedSearch
                });
            } catch (error) {
                if (isLatest()) setListLoading(false);
                errorHandle(error);
            }
        }
        fetchPackList();
    }, [serviceType, selectedOrg, selectedStatus, limit, offSet, datesType, group, load, debouncedSearch]);

    useEffect(() => {
        if (!selectedOrg || !servicePackListConfig?.countOfPackages) return;
        try {
            servicePackListConfig.countOfPackages({
                setTotalCount,
                selectedOrg,
                selectedStatus,
                datesType,
                group,
                setLoading: () => {},
                serviceType,
                searchRe: debouncedSearch
            });
        } catch (error) {
            errorHandle(error);
        }
    }, [serviceType, selectedStatus, selectedOrg, datesType, group, debouncedSearch]);

    const handleSelect = (id) => {
        const url = `/support/${mode}/details/${selectedOrg}/${id}`;
        window.open(url, '_blank');
    };

    const handleEdit = (id, type = 'edit') => {
        const url = `/support/${mode}/create/pack/${orgId}?new=false&type=${type}&id=${id}`;
        window.open(url, '_blank');
    };

    const handleDuplicate = (id, type = 'duplicate') => {
        const url = `/support/${mode}/create/pack/${orgId}?new=false&type=${type}&id=${id}`;
        window.open(url, '_blank');
    };

    const handleDelete = async (id, type) => {
        setShowModal(true);
        setDeleteId(id);
        if (type) {
            try {
                const result = await supOrgTripDelFun(type, id);

                if (result.status === 200) {
                    toast.success('Activity deleted successfully');
                    setShowModal(false);
                    refresh();
                } else {
                    toast.error('Failed to delete the package');
                }
            } catch (error) {
                errorHandle(error);
                return null;
            }
        }
    };

    const handleInactive = (id) => {
        setInactiveId(id);
        setShowInactiveStatusModal(true);
    };

    const confirmInactive = async () => {
        try {
            const data = {
                type: serviceType,
                _id: inactiveId,
                status: 'inactive'
            };

            const result = await supBookingStatusChange(data);

            if (result.status === 200) {
                toast.success('Package Inactivated successfully');
                setShowInactiveStatusModal(false);
                setInactiveId(null);
                refresh();
            }
        } catch (error) {
            errorHandle(error);
        }
    };

    if (nav && selectedPack) {
        return <Navigate to={`/support/${mode}/details/${selectedOrg}/${selectedPack}`} />;
    }

    return (
        <div>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='orgPack'>
                {/* discuss on whether we need filters in mybooking */}
                {/* <Filters filterData={filterDummy?.data} /> */}
                {/* <Filters filterData={[]} /> */}
                <div className='orgPackTripStrap'>
                    <div className='orgPackHeadSeaDiv alignCenter supportPack'>
                        <h1 className='orgBookingHeading'>My Packages</h1>
                        <div>
                            {/* <button
                                type="button"
                                onClick={() =>
                                    orgPackSearch(searchText, setSearchRe)
                                }
                                className="orgPackSearchBtn"
                            >
                                Search
                            </button> */}
                        </div>
                        <div>
                            <div className='search-input'>
                                <img src={SearchIcon} alt='searchIcon' className='search-icon' />
                                <input
                                    placeholder='Search your packages'
                                    name='orgSearch'
                                    value={searchRe}
                                    className='select mr-2 searchBarInput'
                                    onChange={(e) => setSearchRe(e.target.value)}
                                />
                            </div>
                            <select
                                name='serviceType'
                                id='serviceType'
                                className='select mr-2'
                                onChange={handleServiceType}
                                value={serviceType}
                            >
                                <option value='trip'>Trip</option>
                                <option value='act'>Activity</option>
                                <option value='gu'>Guide</option>
                                <option value='camp'>Camp</option>
                            </select>
                            <select
                                name='org'
                                id='org'
                                className='select mr-2'
                                value={selectedOrg}
                                onChange={(e) => {
                                    const selectedId = e.target.value;

                                    if (selectedId) {
                                        const params = new URLSearchParams();
                                        params.set('status', selectedStatus);
                                        // add datesType only for trip
                                        if (mode === 't') {
                                            params.set('datesType', datesType);
                                            params.set('group', group);
                                        }
                                        params.set('page', 1);
                                        navigate(`/support/${mode}/packages/${selectedId}?${params.toString()}`);
                                    }
                                }}
                            >
                                <option value=''>Select Organizer</option>
                                {orgData.map((org) => (
                                    <option value={org._id} key={org._id}>
                                        {org.companyName}
                                    </option>
                                ))}
                            </select>
                            <select
                                name='date'
                                id='date'
                                className='select mr-2'
                                onChange={(e) => {
                                    updateURL({ status: e.target.value, page: 1 });
                                }}
                                value={selectedStatus}
                            >
                                <option value='active'>Approved</option>
                                <option value='inactive'>Not Approved</option>
                                <option value='rejected'>Rejected</option>
                            </select>
                            {serviceType === 'trip' ? (
                                <>
                                    <select
                                        name='datesType'
                                        id='datesType'
                                        className='select mr-2'
                                        onChange={(e) => {
                                            updateURL({ datesType: e.target.value, page: 1 });
                                        }}
                                        value={datesType}
                                    >
                                        <option value='fixed'>Fixed</option>
                                        <option value='custom'>Custom</option>
                                    </select>
                                    <select
                                        name='group'
                                        id='group'
                                        className='select mr-2'
                                        onChange={(e) => {
                                            updateURL({ group: e.target.value, page: 1 });
                                        }}
                                        value={group}
                                    >
                                        <option value='backpacking'>Backpacking</option>
                                        <option value='vehicle'>Vehicle</option>
                                    </select>
                                </>
                            ) : null}
                            {/* <button className='orgPackCreateBtn blueBtn' onClick={() => navigate('/org/create/pack')}>
                                Create {window.innerWidth < 768 ? '' : 'Package'}
                            </button> */}
                        </div>
                    </div>

                    {listLoading ? (
                        <div className='supPackListLoading' role='status' aria-live='polite'>
                            <ReactLoading type='spin' color='#006aff' height={36} width={36} />
                            <span>Loading packages…</span>
                        </div>
                    ) : !tripData.data?.length ? (
                        selectedOrg ? <EmptyPage /> : null
                    ) : (
                        <>
                            {serviceType === 'trip' ? (
                                <SupTripStrap
                                    page={'orgPackList'}
                                    setNav={setNav}
                                    data={tripData.data}
                                    setSelectedPack={setSelectedPack}
                                    selectedStatus={selectedStatus}
                                    refresh={refresh}
                                    serviceType={serviceType}
                                />
                            ) : serviceType === 'camp' ? (
                                <div className='accommodation-list'>
                                    {tripData?.data?.map((camp) => (
                                        <AccommodationCard
                                            key={camp._id}
                                            accommodation={camp}
                                            onView={handleSelect}
                                            onEdit={handleEdit}
                                            onDuplicate={handleDuplicate}
                                            onDelete={handleDelete}
                                            onReject={handleInactive}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <OrgTripList
                                    data={tripData.data}
                                    selectedStatus={selectedStatus}
                                    onSelect={handleSelect}
                                    onEdit={handleEdit}
                                    onDuplicate={handleDuplicate}
                                    onDelete={handleDelete}
                                    onInactive={handleInactive}
                                />
                            )}

                            <Pagination
                                paginationClick={paginationClick}
                                numberOfRows={totalData}
                                offSet={offSet}
                                pageNumber={pageNumber}
                                limit={limit}
                            />
                        </>
                    )}
                    {showInactiveStatusModal && (
                        <ConfirmationModal
                            setShowModal={setShowInactiveStatusModal}
                            handleConfirm={confirmInactive}
                            message={'Do you want to make guide package as incative?'}
                        />
                    )}
                    {showModal && (
                        <ConfirmationModal
                            setShowModal={setShowModal}
                            handleConfirm={() => handleDelete(deleteId, serviceType)}
                            message={'Do you want to delete the activity?'}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
